/**
 * <goody-brand-upsell> — "More from {brand}" upsell grid for on-Goody PDPs.
 *
 * Renders below the product-detail accordions (blocks/_goody-brand-upsell.liquid,
 * placed inside _product-details). Unlike the cart-driven <goody-upsell>, the
 * vendor here is FIXED server-side to the current PDP product's vendor — it is
 * NOT cart-dependent and shows regardless of cart contents.
 *
 * Products are sourced from the automatic vendor collection
 * (/collections/vendors?q=<vendor>) rendered through the Section Rendering API
 * (sections/ea-goody-upsell-data.liquid) — exact vendor match, immune to
 * search-hiding (seo.hidden). The current product is excluded. When no other
 * products exist for the vendor the component stays hidden.
 *
 * Data attributes (set by blocks/_goody-brand-upsell.liquid):
 *   data-vendor         — the PDP product's vendor (exact-match filter)
 *   data-exclude-id     — current product id (removed from the grid)
 *   data-max-products   — max cards to show (default 4)
 *   data-money-format   — shop money_format string (e.g. "${{amount}}")
 *   data-currency       — shop currency ISO code (e.g. "USD"), for formatMoney
 *   data-add-label      — localized "Add to cart" label
 *   data-soldout-label  — localized "Sold out" label
 *   data-adding-label   — localized "Adding..." label (quick add in progress)
 *   data-added-label    — localized "Added" label (quick add success)
 *
 * Add-to-cart mirrors <goody-upsell>:
 * - Multi-variant products delegate to the personalization drawer via the
 *   [data-add-to-cart] wrapper + data-product-handle carrier on each card.
 * - EVERY card routes through the personalization drawer via the [data-add-to-cart]
 *   wrapper + data-product-handle carrier. Single-variant products used to add
 *   straight to the cart from here, which skipped the three mandatory Goody gates
 *   the drawer applies (fulfillment switch, restricted states, digital card) — see
 *   the note on #cardHtml.
 */
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const DATA_SECTION_ID = 'ea-goody-upsell-data';
// Max cards rendered on desktop (3-col grid = one row). Mobile hides the 3rd
// via CSS so only 2 show.
const GBU_DESKTOP_MAX = 3;

class GoodyBrandUpsell extends HTMLElement {
  /** @type {AbortController|null} */
  #abortController = null;

  connectedCallback() {

    const vendor = (this.dataset.vendor || '').trim();
    if (vendor) this.#refresh(vendor);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }


  /* ------------------------------------------------------------------ */
  /*  Fetch + render                                                     */
  /* ------------------------------------------------------------------ */

  /** @param {string} vendor */
  async #refresh(vendor) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      this.#showSkeleton();
      const products = await this.#fetchVendorCollection(vendor, signal);
      const excludeId = parseInt(this.dataset.excludeId, 10) || 0;
      // Hard-cap at the desktop maximum (3). Mobile shows only 2 — the 3rd
      // card is hidden via CSS (max-width: 749px) so no resize handling is
      // needed. A lower merchant max_products setting still wins.
      const maxProducts = Math.min(parseInt(this.dataset.maxProducts, 10) || GBU_DESKTOP_MAX, GBU_DESKTOP_MAX);

      const filtered = products
        .filter((p) => p.id !== excludeId)
        .slice(0, maxProducts);

      if (!filtered.length) {
        this.#hide();
        return;
      }

      this.#render(filtered);
      this.#show();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[goody-brand-upsell] refresh error:', err);
      this.#hide();
    }
  }

  /**
   * @param {string} vendor
   * @param {AbortSignal} signal
   * @returns {Promise<Array>} normalized products
   */
  async #fetchVendorCollection(vendor, signal) {
    const root = window.Shopify?.routes?.root || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const url = new URL(`${base}collections/vendors`, window.location.origin);
    url.searchParams.set('q', vendor);
    url.searchParams.set('section_id', DATA_SECTION_ID);

    const res = await fetch(url.href, { signal });
    if (!res.ok) throw new Error(`Vendor collection fetch failed: ${res.status}`);

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const island = doc.querySelector('[data-goody-upsell-data]');
    if (!island) return [];

    let raw;
    try {
      raw = JSON.parse(island.textContent);
    } catch {
      return [];
    }
    if (!Array.isArray(raw)) return [];

    return raw
      .filter((p) => p.vendor?.toLowerCase() === vendor.toLowerCase())
      .map((p) => ({
        id: p.id,
        handle: p.handle,
        title: p.title,
        url: p.url,
        vendor: p.vendor || '',
        image: p.image || '',
        price: p.price,
        compareAtPrice: p.compare_at_price || 0,
        promotionalMessage: p.promotional_message || '',
        variantId: p.variant_id,
        variantsCount: p.variants_count,
        externalVariantId: p.external_variant_id || '',
        arrangementId: p.arrangement_id || '',
        available: p.available !== false,
      }));
  }

  /** @param {Array} products */
  #render(products) {
    const grid = this.querySelector('.gbu__grid');
    if (!grid) return;
    grid.innerHTML = products.map((p) => this.#renderCard(p)).join('');
  }

  /**
   * @param {Object} product — normalized product (see #fetchVendorCollection)
   * @returns {string}
   */
  #renderCard(product) {
    const image = product.image || '';
    const title = this.#escapeHtml(product.title || '');
    const url = product.url || '';
    const price = product.price;
    const compareAtPrice = product.compareAtPrice;
    const variantId = product.variantId;
    const available = product.available;
    const addLabel = this.#escapeHtml(this.dataset.addLabel || 'Add to cart');
    const soldOutLabel = this.#escapeHtml(this.dataset.soldoutLabel || 'Sold out');

    let priceHtml = `<span class="gbu__price">${this.#formatMoney(price)}</span>`;
    if (compareAtPrice && compareAtPrice > price) {
      priceHtml += `<span class="gbu__compare-price">${this.#formatMoney(compareAtPrice)}</span>`;
    }

    const promoHtml = product.promotionalMessage
      ? `<p class="gbu__promo">${this.#escapeHtml(product.promotionalMessage)}</p>`
      : '';

    const handle = this.#escapeHtml(product.handle || '');

    // EVERY card here is a Goody product (the card carries data-on-goody="true"), and a
    // Goody add has three mandatory gates that only the personalization drawer applies:
    //
    //   1. checkGoodyFulfillment() — switches the selection to `shipment` and clears
    //      store-bound items first. A Goody order is shipped by the marketplace, so
    //      leaving the selection on delivery/pickup puts the wrong `order_type` on the
    //      order and suppresses the carrier-rate fee (#resolveGoodyDeliveryFee returns
    //      null for pickup).
    //   2. #restrictedStateName() — refuses a product the marketplace will not ship to
    //      the selected state (AK/HI in practice, from the product's restricted_states
    //      metafield). Without it an undeliverable line reaches checkout.
    //   3. The mandatory digital card — #updateAtcButtonState keeps Add to Cart disabled
    //      for an on-goody product until a card AND a valid recipient are provided.
    //
    // So a one-click add could never produce a COMPLETE Goody line: (3) alone means the
    // quick path was shipping lines the drawer would have refused. Rather than duplicate
    // three gates (one of which needs the drawer's own UI), single-variant cards now use
    // the same [data-add-to-cart] wrapper as multi-variant ones. The drawer handles a
    // single-variant product fine — it renders one variant card.
    const buttonHtml = `<div data-add-to-cart>
          <button
            type="button"
            class="gbu__atc"
            data-variant-id="${variantId}"
            ${!available ? 'disabled' : ''}
          >${available ? addLabel : soldOutLabel}</button>
        </div>`;

    // data-on-goody="true": the cart-conflict interceptor classifies the add as
    // on-Goody (else it 409s against a goody cart). data-product-handle: PD
    // product resolution for the multi-variant path. No per-card data-vendor is
    // needed for the interceptor's same-vendor check: the <goody-brand-upsell>
    // HOST carries data-vendor and every card here is exact-vendor-matched to it,
    // so resolveVendorForVariant's closest('[data-vendor]') walk lands on the host.
    return `
      <div class="gbu__card" role="listitem" data-on-goody="true" data-product-handle="${handle}">
        <a href="${url}" class="gbu__card-link" aria-label="${title}">
          <div class="gbu__image">
            ${image ? `<img src="${this.#resizeImage(image, 320)}" alt="${title}" width="160" height="160" loading="lazy">` : '<div class="gbu__image-placeholder"></div>'}
          </div>
          <p class="gbu__title">${title}</p>
          <div class="gbu__prices">${priceHtml}</div>
          ${promoHtml}
        </a>
        ${buttonHtml}
      </div>
    `;
  }


  /* ------------------------------------------------------------------ */
  /*  Visibility                                                         */
  /* ------------------------------------------------------------------ */

  #showSkeleton() {
    const grid = this.querySelector('.gbu__grid');
    if (!grid) return;
    const count = Math.min(parseInt(this.dataset.maxProducts, 10) || GBU_DESKTOP_MAX, GBU_DESKTOP_MAX);
    grid.innerHTML = Array.from({ length: count }, () => `
      <div class="gbu__card gbu__skeleton">
        <div class="gbu__image"></div>
        <div class="gbu__skeleton-line" style="width:80%;height:14px;margin-top:12px"></div>
        <div class="gbu__skeleton-line" style="width:40%;height:14px;margin-top:8px"></div>
        <div class="gbu__skeleton-line" style="width:118px;height:32px;margin-top:12px;border-radius:24px"></div>
      </div>
    `).join('');
    this.#show();
  }

  #show() {
    this.setAttribute('data-has-products', '');
  }

  #hide() {
    this.removeAttribute('data-has-products');
    const grid = this.querySelector('.gbu__grid');
    if (grid) grid.innerHTML = '';
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Format minor units (cents) into a currency string via the shared
   * @theme/money-formatting helper — replicates the Liquid `money` filter,
   * handling every amount placeholder variant and currency precision.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    return formatMoney(cents, moneyFormat, currency);
  }

  /**
   * Attempt to resize a Shopify CDN image URL to a given width.
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #resizeImage(url, width) {
    if (!url) return '';
    try {
      if (url.includes('/cdn/') || url.includes('.shopify.com')) {
        return url.replace(/(_\d+x\d*|\d+x\d*)?(\.(jpg|jpeg|png|gif|webp|avif))/i, `_${width}x$2`);
      }
    } catch {
      // fall through
    }
    return url;
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('goody-brand-upsell', GoodyBrandUpsell);
